import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import BRAND from '../config/brand';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import { CONTENT_CATEGORIES } from '../config/planningOffers';
import './IncomeChangePaycheckTaxEstimator.css';
import './RothTraditionalCalculator.css';
import './RetirementGapEstimator.css';
import { estimateAnnualSocialSecurityQuick } from './SocialSecurityEstimator';

const CHART_END_AGE = 95;
const NOMINAL_RETURN = 0.09;
const PRE_RETIREMENT_NOMINAL_RETURN = 0.07;
const GLIDE_PATH_YEARS = 5;
const INFLATION = 0.025;
const WITHDRAWAL_RATE = 0.04;
const REAL_RETURN = (1 + NOMINAL_RETURN) / (1 + INFLATION) - 1;
const PRE_RETIREMENT_REAL_RETURN = (1 + PRE_RETIREMENT_NOMINAL_RETURN) / (1 + INFLATION) - 1;
// Conservative real return during withdrawals — illustrative decumulation in today's dollars.
const WITHDRAWAL_PHASE_REAL_RETURN = 0.02;
const SUSTAINABLE_BALANCE_TOLERANCE = 25000;

const PLANNING_ASSUMPTIONS_COPY = [
  'This calculator uses a 9% long-term investment return assumption and 2.5% inflation. All figures are shown in today\'s dollars.',
  'Starting five years before your planned retirement age, the return assumption shifts to 7% to reflect a typical move toward a more conservative portfolio mix.',
  'Retirement planning is modeled through age 95 using two paths: your desired lifestyle spending, and the income your current savings can sustainably support for life.',
];

const DEFAULTS = {
  filingStatus: 'single',
  currentAge: '40',
  retirementAge: '65',
  annualIncome: '150000',
  spouseIncome: '',
  currentSavings: '250000',
  personalContribution: '12000',
  employerContribution: '6000',
  spousePersonalContribution: '',
  spouseEmployerContribution: '',
  targetIncome: '100000',
  otherIncome: '',
};

const SS_QUICK_DISCLAIMER =
  'Quick estimate from salary using simplified Social Security formulas. Actual benefits depend on your earnings record, claiming age, cost-of-living adjustments, taxes, Medicare premiums, and SSA rules — and are subject to change.';

const OUTLOOK_SCENARIOS = {
  severely_underfunded: {
    headline: 'Retirement Goal Needs Attention',
    body: 'Your current savings path is unlikely to fully support your desired retirement lifestyle. The good news is that there are multiple ways to improve the outcome before retirement.',
    opportunities: [
      'Increase long-term savings',
      'Delay retirement or reduce retirement spending',
      'Improve tax efficiency and investment strategy',
    ],
    className: 'rge-outlook--severe',
  },
  moderately_underfunded: {
    headline: "You're Close",
    body: 'Your retirement plan is within reach, but modest planning adjustments could significantly improve long-term income security.',
    opportunities: [
      'Increase annual savings',
      'Optimize retirement account contributions',
      'Review retirement timing and Social Security strategy',
    ],
    className: 'rge-outlook--moderate',
  },
  on_track: {
    headline: 'Your Retirement Plan Is On Track',
    body: 'Based on today\'s assumptions, your current strategy appears capable of supporting your retirement income goal through age 95.',
    opportunities: [
      'Stay consistent with savings',
      'Monitor progress annually',
      'Adjust as tax laws and life change',
    ],
    className: 'rge-outlook--on-track',
  },
  oversaving: {
    headline: 'You May Have More Flexibility Than Expected',
    body: 'Your current savings path appears to exceed your retirement income goal. That may create opportunities to improve your quality of life today without sacrificing long-term security.',
    opportunities: [
      'Consider retiring earlier',
      'Increase current lifestyle spending',
      'Redirect savings toward other financial goals',
    ],
    className: 'rge-outlook--oversaving',
  },
};

const OUTLOOK_DISCLAIMER = 'This illustration is based on the assumptions entered and does not replace a comprehensive financial plan.';

const PLANNING_INSIGHTS = [
  {
    title: 'Income replacement',
    body: 'Many people focus on their account balance. Retirement planning is really about replacing income. A $2 million portfolio means something very different depending on how much income you actually need.',
  },
  {
    title: 'Sequence of returns',
    body: 'Two investors with identical balances can experience very different retirement outcomes depending on when market declines occur.',
  },
  {
    title: 'Inflation',
    body: "Retirement planning isn't just reaching a number. Your purchasing power matters just as much as your account balance.",
  },
  {
    title: 'Taxes',
    body: 'Traditional and Roth accounts create very different retirement cash flows. Tax planning often matters as much as investment returns.',
  },
];

const CTA_QUESTIONS = [
  'Are you saving in the right accounts?',
  'Traditional or Roth?',
  'Can you retire earlier?',
  'Are taxes creating hidden retirement drag?',
  'Is your investment allocation appropriate?',
  'Will your spending support your goals?',
  'Are you taking unnecessary retirement risk?',
];

function formatUsd(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatUsdCompact(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1000000) return `$${(value / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value)}`;
}

function formatPct(value, digits = 0) {
  if (value == null || !Number.isFinite(value)) return '--';
  return `${(value * 100).toFixed(digits)}%`;
}

function parsePositiveNumber(value) {
  if (value === '' || value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric;
}

function parseContribution(value) {
  if (value === '' || value == null) return 0;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric;
}

function parseOptionalNumber(value) {
  if (value === '' || value == null) return 0;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric;
}

export function getAccumulationRealReturn(age, retirementAge) {
  const glideStartAge = retirementAge - GLIDE_PATH_YEARS;
  if (age >= glideStartAge && age < retirementAge) {
    return PRE_RETIREMENT_REAL_RETURN;
  }
  return REAL_RETURN;
}

function projectBalanceAtRetirement(currentSavings, annualContribution, currentAge, retirementAge) {
  let balance = currentSavings;
  for (let age = currentAge + 1; age <= retirementAge; age += 1) {
    const rate = getAccumulationRealReturn(age, retirementAge);
    balance = balance * (1 + rate) + annualContribution;
  }
  return balance;
}

function measureWithdrawalOutcome(startBalance, annualWithdrawal, retirementAge) {
  let balance = startBalance;
  let depletedAge = null;

  for (let age = retirementAge + 1; age <= CHART_END_AGE; age += 1) {
    if (balance <= 0) {
      depletedAge = age - 1;
      break;
    }
    const withdrawal = Math.min(balance, annualWithdrawal);
    balance = Math.max(0, (balance - withdrawal) * (1 + WITHDRAWAL_PHASE_REAL_RETURN));
    if (balance <= 0) {
      depletedAge = age;
      break;
    }
  }

  const depletedEarly = depletedAge != null && depletedAge < CHART_END_AGE;
  return {
    balanceAt95: balance,
    depletedEarly,
    depletedAge,
  };
}

function findMaxSustainableAnnualWithdrawal(balanceAtRetirement, measureWithdrawal) {
  if (balanceAtRetirement <= 0) return 0;

  const endsNearZeroAt95 = (withdrawal) => {
    const { balanceAt95, depletedEarly } = measureWithdrawal(withdrawal);
    return !depletedEarly && balanceAt95 <= SUSTAINABLE_BALANCE_TOLERANCE;
  };
  const survivesTo95 = (withdrawal) => !measureWithdrawal(withdrawal).depletedEarly;

  let lo = 0;
  let hi = Math.max(1000, balanceAtRetirement * WITHDRAWAL_RATE);
  let expansions = 0;
  while (!endsNearZeroAt95(hi) && survivesTo95(hi) && expansions < 12) {
    hi *= 1.5;
    expansions += 1;
  }

  if (!survivesTo95(hi)) {
    while (hi - lo > 50) {
      const mid = (lo + hi) / 2;
      if (survivesTo95(mid)) lo = mid;
      else hi = mid;
    }
    return Math.round(lo);
  }

  if (!endsNearZeroAt95(hi)) {
    return Math.round(hi);
  }

  while (hi - lo > 50) {
    const mid = (lo + hi) / 2;
    if (endsNearZeroAt95(mid)) lo = mid;
    else hi = mid;
  }
  return Math.round(lo);
}

function buildDualSeries({
  currentAge,
  retirementAge,
  currentSavings,
  annualContribution,
  desiredWithdrawal,
  sustainableWithdrawal,
}) {
  const endAge = CHART_END_AGE;
  let desiredBalance = currentSavings;
  let sustainableBalance = currentSavings;
  let desiredDepletedAge = null;
  const series = [];

  for (let age = currentAge; age <= endAge; age += 1) {
    if (age > currentAge && age <= retirementAge) {
      const rate = getAccumulationRealReturn(age, retirementAge);
      desiredBalance = desiredBalance * (1 + rate) + annualContribution;
      sustainableBalance = sustainableBalance * (1 + rate) + annualContribution;
    } else if (age > retirementAge) {
      if (desiredDepletedAge == null && desiredBalance > 0) {
        const withdrawal = Math.min(desiredBalance, desiredWithdrawal);
        desiredBalance = Math.max(0, (desiredBalance - withdrawal) * (1 + WITHDRAWAL_PHASE_REAL_RETURN));
        if (desiredBalance <= 0) {
          desiredBalance = 0;
          desiredDepletedAge = age;
        }
      }

      if (sustainableBalance > 0) {
        const withdrawal = Math.min(sustainableBalance, sustainableWithdrawal);
        sustainableBalance = Math.max(0, (sustainableBalance - withdrawal) * (1 + WITHDRAWAL_PHASE_REAL_RETURN));
      }
    }

    series.push({
      age,
      desiredBalance: desiredDepletedAge != null && age > desiredDepletedAge ? null : desiredBalance,
      sustainableBalance,
    });
  }

  return { series, desiredDepletedAge };
}

function getOutlookScenario(fundingPercent) {
  if (fundingPercent < 75) return 'severely_underfunded';
  if (fundingPercent < 95) return 'moderately_underfunded';
  if (fundingPercent <= 105) return 'on_track';
  return 'oversaving';
}

function buildIncomeNarrative(projection) {
  const {
    sustainablePortfolioIncome,
    investmentIncomeRequired,
    annualFundingGap,
    desiredDepletionAge,
    lastsThrough95,
    socialSecurity,
    otherIncome,
  } = projection;

  const guaranteedLabel = formatUsd(socialSecurity + otherIncome);
  let narrative = `Based on your current savings rate, your portfolio can sustainably provide approximately ${formatUsd(sustainablePortfolioIncome)} per year in today's dollars through age ${CHART_END_AGE}.`;

  if (otherIncome > 0) {
    narrative += ` Together with Social Security and other guaranteed income (${guaranteedLabel}/yr), your current path supports about ${formatUsd(projection.sustainableTotalIncome)}/yr toward your goal.`;
  } else {
    narrative += ` Together with Social Security (${formatUsd(socialSecurity)}/yr), your current path supports about ${formatUsd(projection.sustainableTotalIncome)}/yr toward your goal.`;
  }

  if (investmentIncomeRequired > 0) {
    narrative += ` Your desired retirement lifestyle requires approximately ${formatUsd(investmentIncomeRequired)} annually from investments`;
    if (annualFundingGap > 0) {
      narrative += `, creating an estimated annual funding gap of ${formatUsd(annualFundingGap)}.`;
    } else {
      narrative += '.';
    }
  }

  if (!lastsThrough95 && desiredDepletionAge != null) {
    narrative += ` Unless savings increase, retirement is delayed, spending decreases, or other income sources are added, the desired withdrawal strategy would deplete the portfolio before age ${CHART_END_AGE}.`;
  } else if (lastsThrough95) {
    narrative += ' Under these assumptions, your desired lifestyle appears fundable through the full planning horizon.';
  }

  return narrative;
}

function portfolioFundingStatus(projection, rowType) {
  if (rowType === 'social_security') {
    return projection.socialSecurity > 0
      ? { label: 'Estimated', className: 'rge-status--funded' }
      : { label: 'Not entered', className: 'rge-status--gap' };
  }
  if (rowType === 'other') {
    return projection.otherIncome > 0
      ? { label: 'On file', className: 'rge-status--funded' }
      : { label: 'Not entered', className: 'rge-status--gap' };
  }
  if (rowType === 'goal') {
    if (projection.lastsThrough95 && projection.annualFundingGap <= 0) {
      return { label: '✓ Fully Funded', className: 'rge-status--funded' };
    }
    if (!projection.lastsThrough95) {
      return { label: '⚠ Portfolio Depletes Before Age 95', className: 'rge-status--gap' };
    }
    return { label: '⚠ Income Gap', className: 'rge-status--partial' };
  }
  if (rowType === 'supported') {
    if (projection.annualFundingGap <= 0) {
      return { label: '✓ On Track', className: 'rge-status--funded' };
    }
    return { label: '⚠ Income Gap', className: 'rge-status--partial' };
  }
  if (projection.fundingPercent >= 95 && projection.fundingPercent <= 105) {
    return { label: '✓ On Track', className: 'rge-status--funded' };
  }
  if (projection.fundingPercent > 105) {
    return { label: '✓ Fully Funded', className: 'rge-status--funded' };
  }
  return { label: '⚠ Income Gap', className: 'rge-status--partial' };
}

function overallFundingStatus(projection) {
  if (projection.lastsThrough95 && projection.annualFundingGap <= 0) {
    return { label: '✓ Fully Funded', className: 'rge-status--funded' };
  }
  if (!projection.lastsThrough95) {
    return { label: '⚠ Portfolio Depletes Before Age 95', className: 'rge-status--gap' };
  }
  if (projection.fundingPercent >= 95) {
    return { label: '✓ On Track', className: 'rge-status--funded' };
  }
  return { label: '⚠ Income Gap', className: 'rge-status--partial' };
}

export function retirementGapCategory(projection) {
  if (!projection) return undefined;
  const scenario = getOutlookScenario(projection.fundingPercent);
  if (scenario === 'on_track') return 'on_track';
  if (scenario === 'moderately_underfunded') return 'moderate_gap';
  if (scenario === 'severely_underfunded') return 'high_gap';
  return 'oversaving';
}

export function getReadinessLevel(fundingPercent) {
  const scenario = getOutlookScenario(fundingPercent);
  if (scenario === 'on_track' || scenario === 'oversaving') return 'excellent';
  if (scenario === 'moderately_underfunded') return 'moderate';
  return 'large';
}

function totalContributions(raw, isMarried) {
  const personal = parseContribution(raw.personalContribution);
  const employer = parseContribution(raw.employerContribution);
  const spousePersonal = isMarried ? parseContribution(raw.spousePersonalContribution) : 0;
  const spouseEmployer = isMarried ? parseContribution(raw.spouseEmployerContribution) : 0;
  if ([personal, employer, spousePersonal, spouseEmployer].some((v) => v == null)) return null;
  return personal + employer + spousePersonal + spouseEmployer;
}

function buildProjection(raw) {
  const isMarried = raw.filingStatus === 'married';
  const currentAge = parsePositiveNumber(raw.currentAge);
  const retirementAge = parsePositiveNumber(raw.retirementAge);
  const currentSavings = parsePositiveNumber(raw.currentSavings);
  const annualContribution = totalContributions(raw, isMarried);
  const targetIncome = parsePositiveNumber(raw.targetIncome);
  const primaryIncome = parseOptionalNumber(raw.annualIncome);
  const spouseIncome = isMarried ? parseOptionalNumber(raw.spouseIncome) : 0;
  const otherIncome = parseOptionalNumber(raw.otherIncome);

  if (
    [currentAge, retirementAge, currentSavings, annualContribution, targetIncome].some((v) => v == null)
    || primaryIncome == null
    || spouseIncome == null
    || otherIncome == null
    || currentAge < 18
    || retirementAge <= currentAge
  ) {
    return null;
  }

  const householdIncome = primaryIncome + (isMarried ? spouseIncome : 0);
  const socialSecurity = estimateAnnualSocialSecurityQuick({
    primaryIncome,
    spouseIncome: isMarried ? spouseIncome : 0,
    retirementAge,
    filingStatus: raw.filingStatus,
  });

  const yearsToRetirement = retirementAge - currentAge;
  const projectedBalance = projectBalanceAtRetirement(
    currentSavings,
    annualContribution,
    currentAge,
    retirementAge,
  );
  const investmentIncomeRequired = Math.max(0, targetIncome - socialSecurity - otherIncome);
  const measureWithdrawal = (withdrawal) => measureWithdrawalOutcome(
    projectedBalance,
    withdrawal,
    retirementAge,
  );
  const sustainablePortfolioIncome = findMaxSustainableAnnualWithdrawal(
    projectedBalance,
    measureWithdrawal,
  );
  const sustainableTotalIncome = sustainablePortfolioIncome + socialSecurity + otherIncome;
  const annualFundingGap = Math.max(0, investmentIncomeRequired - sustainablePortfolioIncome);
  const gap = Math.max(0, targetIncome - sustainableTotalIncome);
  const fundingPercent = targetIncome > 0
    ? Math.min((sustainableTotalIncome / targetIncome) * 100, 999)
    : 0;
  const savingsRate = householdIncome > 0
    ? annualContribution / householdIncome
    : (targetIncome > 0 ? annualContribution / targetIncome : 0);

  const desiredOutcome = measureWithdrawal(investmentIncomeRequired);
  const lastsThrough95 = !desiredOutcome.depletedEarly
    && desiredOutcome.balanceAt95 <= SUSTAINABLE_BALANCE_TOLERANCE;

  const { series: dualSeries, desiredDepletedAge } = buildDualSeries({
    currentAge,
    retirementAge,
    currentSavings,
    annualContribution,
    desiredWithdrawal: investmentIncomeRequired,
    sustainableWithdrawal: sustainablePortfolioIncome,
  });

  const outlookScenario = getOutlookScenario(fundingPercent);

  return {
    filingStatus: raw.filingStatus,
    yearsToRetirement,
    projectedBalance,
    portfolioIncome: sustainablePortfolioIncome,
    estimatedTotalIncome: sustainableTotalIncome,
    sustainablePortfolioIncome,
    sustainableTotalIncome,
    investmentIncomeRequired,
    annualFundingGap,
    targetIncome,
    gap,
    fundingPercent,
    incomeReplacementPercent: fundingPercent,
    savingsRate,
    socialSecurity,
    otherIncome,
    householdIncome,
    primaryIncome,
    spouseIncome: isMarried ? spouseIncome : 0,
    readiness: getReadinessLevel(fundingPercent),
    dualSeries,
    currentAge,
    retirementAge,
    annualContribution,
    currentSavings,
    socialSecurityClaimAge: Math.min(70, Math.max(62, retirementAge)),
    desiredDepletionAge: desiredDepletedAge,
    lastsThrough95,
    outlookScenario,
    balanceAtRetirement: projectedBalance,
    retirementYears: CHART_END_AGE - retirementAge,
  };
}

function solveExtraContribution(projection) {
  const {
    currentSavings,
    annualContribution,
    targetIncome,
    socialSecurity,
    otherIncome,
    currentAge,
    retirementAge,
  } = projection;

  if (projection.gap <= 0) return null;

  let low = 0;
  let high = Math.max(projection.gap * 5, annualContribution);
  for (let i = 0; i < 40; i += 1) {
    const mid = (low + high) / 2;
    const balance = projectBalanceAtRetirement(
      currentSavings,
      annualContribution + mid,
      currentAge,
      retirementAge,
    );
    const income = findMaxSustainableAnnualWithdrawal(
      balance,
      (withdrawal) => measureWithdrawalOutcome(balance, withdrawal, retirementAge),
    ) + socialSecurity + otherIncome;
    if (income >= targetIncome) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return Math.ceil(high / 100) * 100;
}

function solveRetirementDelay(projection) {
  if (projection.gap <= 0) return null;

  for (let extraYears = 1; extraYears <= 15; extraYears += 1) {
    const delayedRetirementAge = projection.retirementAge + extraYears;
    const balance = projectBalanceAtRetirement(
      projection.currentSavings,
      projection.annualContribution,
      projection.currentAge,
      delayedRetirementAge,
    );
    const income = findMaxSustainableAnnualWithdrawal(
      balance,
      (withdrawal) => measureWithdrawalOutcome(balance, withdrawal, delayedRetirementAge),
    ) + projection.socialSecurity + projection.otherIncome;
    if (income >= projection.targetIncome) return extraYears;
  }
  return null;
}

function isWellFunded(projection) {
  return projection.lastsThrough95 && projection.annualFundingGap <= 0;
}

function buildSuggestions(projection) {
  if (isWellFunded(projection)) {
    return {
      intro: 'Your retirement appears well funded under these assumptions.',
      items: [
        'Tax efficiency across Traditional, Roth, and taxable accounts',
        'Estate planning and beneficiary coordination',
        'Withdrawal strategy and retirement timing',
        'Charitable giving and legacy planning',
      ],
    };
  }

  const extraContribution = solveExtraContribution(projection);
  const delayYears = solveRetirementDelay(projection);
  const reducePercent = projection.targetIncome > 0
    ? Math.ceil((projection.gap / projection.targetIncome) * 100)
    : null;

  const items = [];
  if (extraContribution != null && extraContribution > 0) {
    items.push(`Increase annual savings by about ${formatUsd(extraContribution)}`);
  }
  if (delayYears != null) {
    items.push(`Delay retirement by about ${delayYears} year${delayYears === 1 ? '' : 's'}`);
  }
  if (reducePercent != null && reducePercent > 0) {
    items.push(`Reduce retirement income target by about ${reducePercent}%`);
  }
  items.push(
    'Review Roth vs Traditional strategy',
    'Review investment allocation',
    'Review tax efficiency',
  );

  return {
    intro: 'Based on this estimate, planning opportunities may include:',
    items,
  };
}

function buildRetirementOutlook(projection) {
  return OUTLOOK_SCENARIOS[projection.outlookScenario] || OUTLOOK_SCENARIOS.moderately_underfunded;
}

export default function RetirementGapEstimator() {
  const {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  } = useCalculatorAnalytics({
    calculatorId: 'retirement_gap_estimator',
    calculatorName: 'retirement_projection',
  });

  const [values, setValues] = useState(DEFAULTS);
  const [hoveredAge, setHoveredAge] = useState(null);
  const isMarried = values.filingStatus === 'married';

  const updateField = (analyticsField, stateKey) => (value) => {
    trackFieldInteraction(analyticsField);
    setValues((current) => ({ ...current, [stateKey]: value }));
  };

  const projection = useMemo(() => buildProjection(values), [values]);
  const outlook = projection ? buildRetirementOutlook(projection) : null;
  const incomeNarrative = projection ? buildIncomeNarrative(projection) : null;
  const suggestions = projection ? buildSuggestions(projection) : null;

  useEffect(() => {
    if (!projection) return;
    const params = { result_category: retirementGapCategory(projection) };
    trackResultView(params);
    trackComplete(params);
  }, [projection, trackComplete, trackResultView]);

  const resetCalculator = () => {
    setValues(DEFAULTS);
    setHoveredAge(null);
    trackReset();
  };

  return (
    <div className="pete-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img
              src={BRAND.logoSrc}
              alt="Bautista Planning and Analytics"
              className="pete-logo-img"
              width={440}
              height={88}
              decoding="async"
            />
          </Link>
        </div>
      </header>

      <main className="pete-body">
        <div className="pete-inner">
          <div className="pete-title-block rge-hero">
            <h1>Retirement Gap Estimator</h1>
            <p className="pete-tax-year rge-hero-sub">
              Estimate whether your current savings rate is on pace to support your retirement lifestyle.
            </p>
            <p className="pete-social-calculator-note">
              Coming from TikTok, Reels, or Threads? Use this calculator, then compare your result to
              the decision you&apos;re actually trying to make.
            </p>
          </div>

          <div className="pete-disclaimer-banner" role="note">
            <strong>Educational estimate only.</strong>{' '}
            This calculator uses fixed growth and withdrawal assumptions for planning discussions.
            It is designed to identify potential planning gaps — not replace a personalized retirement analysis.
            Results are subject to change and may not reflect your complete financial picture.
          </div>

          <div className="pete-card">
            <h2>Where Are You Today?</h2>
            <section className="rge-section">
              <h3>Filing status</h3>
              <div className="rtc-toggle" role="group" aria-label="Filing status">
                <button
                  type="button"
                  className={`rtc-toggle-btn ${!isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={!isMarried}
                  onClick={() => updateField('filing_status', 'filingStatus')('single')}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`rtc-toggle-btn ${isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={isMarried}
                  onClick={() => updateField('filing_status', 'filingStatus')('married')}
                >
                  Married
                </button>
              </div>
            </section>
            <section className="rge-section">
              <h3>Timeline &amp; savings</h3>
              <div className={isMarried ? 'rtc-couple-grid' : 'rge-input-grid'}>
                <Field id="rge-current-age" label="Current age" value={values.currentAge} setValue={updateField('current_age', 'currentAge')} />
                <Field id="rge-retirement-age" label="Retirement age" value={values.retirementAge} setValue={updateField('retirement_age', 'retirementAge')} />
                <Field id="rge-annual-income" label={isMarried ? 'Your annual salary' : 'Annual salary'} value={values.annualIncome} setValue={updateField('annual_income', 'annualIncome')} step="1000" />
                {isMarried ? (
                  <Field id="rge-spouse-income" label="Spouse annual salary" value={values.spouseIncome} setValue={updateField('spouse_annual_income', 'spouseIncome')} step="1000" />
                ) : null}
                <Field id="rge-current-savings" label="Current retirement savings" value={values.currentSavings} setValue={updateField('current_savings', 'currentSavings')} step="1000" />
                <Field id="rge-target-income" label="Target annual retirement income" value={values.targetIncome} setValue={updateField('target_income', 'targetIncome')} step="1000" />
                <Field id="rge-other-income" label="Other annual retirement income (optional)" value={values.otherIncome} setValue={updateField('other_income', 'otherIncome')} step="500" />
              </div>
            </section>
            <section className="rge-section">
              <h3>Retirement contributions (if they apply)</h3>
              <p className="rge-assumption-hint">
                Enter personal and employer contributions separately. Leave blank if a category does not apply to you.
              </p>
              <div className={isMarried ? 'rtc-couple-grid' : 'rge-input-grid'}>
                <Field id="rge-personal-contribution" label={isMarried ? 'Your personal contribution' : 'Personal contribution'} value={values.personalContribution} setValue={updateField('personal_contribution', 'personalContribution')} step="500" />
                <Field id="rge-employer-contribution" label={isMarried ? 'Your employer contribution' : 'Employer contribution (if any)'} value={values.employerContribution} setValue={updateField('employer_contribution', 'employerContribution')} step="500" />
                {isMarried ? (
                  <>
                    <Field id="rge-spouse-personal-contribution" label="Spouse personal contribution" value={values.spousePersonalContribution} setValue={updateField('spouse_personal_contribution', 'spousePersonalContribution')} step="500" />
                    <Field id="rge-spouse-employer-contribution" label="Spouse employer contribution" value={values.spouseEmployerContribution} setValue={updateField('spouse_employer_contribution', 'spouseEmployerContribution')} step="500" />
                  </>
                ) : null}
              </div>
              {projection ? (
                <div className="rge-input-grid rtc-calculated-grid">
                  <CalculatedField
                    label="Total annual retirement contributions"
                    value={formatUsd(projection.annualContribution)}
                    hint="Personal plus employer contributions for your household"
                  />
                </div>
              ) : null}
            </section>
            <section className="rge-section">
              <h3>Social Security (quick estimate)</h3>
              <p className="rge-assumption-hint">
                Estimated from salary using simplified formulas at your planned retirement age.
              </p>
              {projection ? (
                <div className="rge-input-grid rtc-calculated-grid">
                  <CalculatedField
                    label="Estimated annual Social Security"
                    value={formatUsd(projection.socialSecurity)}
                    hint={`Assumes claiming at age ${projection.socialSecurityClaimAge} based on salary inputs`}
                  />
                </div>
              ) : (
                <p className="rge-assumption-hint">Enter ages and salary to see a quick Social Security estimate.</p>
              )}
              <p className="rge-ss-disclaimer">{SS_QUICK_DISCLAIMER}</p>
            </section>
            <section className="rge-section">
              <h3>Planning assumptions</h3>
              <div className="rge-assumptions-box" role="note">
                {PLANNING_ASSUMPTIONS_COPY.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
            <button className="pete-secondary-action" type="button" onClick={resetCalculator}>
              Reset calculator
            </button>
          </div>

          {projection ? (
            <>
              <div className="pete-card">
                <h2>Planning Snapshot</h2>
                <p className="pete-summary-text">
                  Retirement planning is about replacing your paycheck for life — not how much is left at age {CHART_END_AGE}.
                  This snapshot compares your desired lifestyle to what your current savings path can sustainably support.
                </p>
                <div className="rge-snapshot-grid">
                  <SnapshotCard
                    label="Income replacement achieved"
                    value={`${Math.round(projection.fundingPercent)}%`}
                    subtitle="How much of your target retirement income your current path may support."
                    accent
                  />
                  <SnapshotCard
                    label="Current portfolio supports"
                    value={`${formatUsd(projection.sustainablePortfolioIncome)}/yr`}
                    subtitle="Maximum annual investment income sustainable through age 95."
                  />
                  <SnapshotCard
                    label="Investment income required"
                    value={`${formatUsd(projection.investmentIncomeRequired)}/yr`}
                    subtitle="Portfolio withdrawals needed to fund your desired lifestyle after guaranteed income."
                  />
                  <SnapshotCard
                    label="Annual funding gap"
                    value={formatUsd(projection.annualFundingGap)}
                    subtitle="Difference between required and sustainable investment income."
                    accent={projection.annualFundingGap > 0}
                  />
                  <SnapshotCard
                    label="Projected depletion"
                    value={projection.lastsThrough95
                      ? `Through age ${CHART_END_AGE}`
                      : `Age ${projection.desiredDepletionAge}`}
                    subtitle={projection.lastsThrough95
                      ? 'Desired lifestyle appears fundable through the planning horizon.'
                      : 'Age when desired spending would deplete the portfolio.'}
                  />
                  <SnapshotCard
                    label="Years until retirement"
                    value={`${projection.yearsToRetirement}`}
                    subtitle="Time remaining for contributions and growth under current assumptions."
                  />
                  <SnapshotCard
                    label="Savings rate"
                    value={formatPct(projection.savingsRate, 1)}
                    subtitle={projection.householdIncome > 0
                      ? 'Total contributions relative to household salary.'
                      : 'Total contributions relative to your target annual retirement income.'}
                  />
                </div>
              </div>

              <div className="pete-card">
                <ProjectionChart
                  series={projection.dualSeries}
                  retirementAge={projection.retirementAge}
                  sustainablePortfolioIncome={projection.sustainablePortfolioIncome}
                  investmentIncomeRequired={projection.investmentIncomeRequired}
                  desiredDepletionAge={projection.desiredDepletionAge}
                  lastsThrough95={projection.lastsThrough95}
                  targetIncome={projection.targetIncome}
                  socialSecurity={projection.socialSecurity}
                  annualFundingGap={projection.annualFundingGap}
                  fundingPercent={projection.fundingPercent}
                  incomeNarrative={incomeNarrative}
                  hoveredAge={hoveredAge}
                  onHover={setHoveredAge}
                />
              </div>

              <div className="pete-card">
                <h2>Income Replacement</h2>
                <p className="pete-summary-text">
                  This table shows where your retirement income may come from — your goal versus what your current savings path can support.
                </p>
                <IncomeReplacementTable projection={projection} />
                <p className="rge-assumption-note">
                  Sustainable investment income is the maximum constant annual withdrawal that lasts through age {CHART_END_AGE}.
                  Social Security: {formatUsd(projection.socialSecurity)}/yr (quick estimate from salary, claiming at age {projection.socialSecurityClaimAge}).
                  {projection.otherIncome > 0 ? ` Other income: ${formatUsd(projection.otherIncome)}/yr.` : ''}
                  {' '}All figures in today&apos;s dollars ({formatPct(NOMINAL_RETURN)} return, shifting to {formatPct(PRE_RETIREMENT_NOMINAL_RETURN)} five years before retirement, {formatPct(INFLATION, 1)} inflation).
                </p>
              </div>

              {outlook ? (
                <div className="pete-card">
                  <div className={`rge-outlook-card ${outlook.className}`} role="status">
                    <p className="rge-outlook-eyebrow">Your Retirement Outlook</p>
                    <p className="rge-outlook-headline">{outlook.headline}</p>
                    <p className="rge-outlook-body">{outlook.body}</p>
                    <p className="rge-outlook-opportunities-label">Planning opportunities</p>
                    <ul className="rge-outlook-opportunities">
                      {outlook.opportunities.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <p className="rge-outlook-disclaimer">{OUTLOOK_DISCLAIMER}</p>
                </div>
              ) : null}

              <div className="pete-card">
                <h2>Planning Insights</h2>
                <div className="rge-insights-grid">
                  {PLANNING_INSIGHTS.map((insight) => (
                    <div key={insight.title} className="rge-insight-card">
                      <h3>{insight.title}</h3>
                      <p>{insight.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pete-card">
                <h2>What Could Improve This?</h2>
                {suggestions ? (
                  <div className="rge-suggestions">
                    <p>{suggestions.intro}</p>
                    <ul>
                      {suggestions.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <CalculatorDiscoveryCta
                eyebrow="Next Step"
                headline="Want help interpreting your result?"
                body={(
                  <>
                    <p>
                      Your result is a starting point. The useful next step is seeing what it means
                      for taxes, savings rate, retirement timing, and spending flexibility.
                    </p>
                    <ul>
                      {CTA_QUESTIONS.map((question) => (
                        <li key={question}>{question}</li>
                      ))}
                    </ul>
                  </>
                )}
                buttonId="retirement_gap_estimator_financial_checkup_cta"
                secondaryButtonId="retirement_gap_estimator_send_result_cta"
                calculatorId="retirement_gap_estimator"
                calculatorName="retirement_projection"
                className="rge-cta"
              />
              <ServiceNextStep
                contentCategory={CONTENT_CATEGORIES.RETIREMENT}
                calculatorId="retirement_gap_estimator"
                calculatorName="retirement_projection"
                promotionalAngle="retirement_calculator"
              />
            </>
          ) : null}

          <p className="pete-footer-note">
            <Link className="pete-portal-link" to="/links">
              Back to planning links
            </Link>
          </p>
        </div>
      </main>

    </div>
  );
}

function Field({ id, label, value, setValue, step = '1' }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
    </div>
  );
}

function CalculatedField({ label, value, hint }) {
  return (
    <div className="pete-field rtc-calculated-field">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

function SnapshotCard({ label, value, subtitle, accent = false }) {
  return (
    <div className={`rge-snapshot-card${accent ? ' rge-snapshot-card--accent' : ''}`}>
      <div className="rge-snapshot-label">{label}</div>
      <div className="rge-snapshot-value">{value}</div>
      <p className="rge-snapshot-sub">{subtitle}</p>
    </div>
  );
}

function IncomeReplacementTable({ projection }) {
  const rows = [
    { source: 'Social Security', amount: projection.socialSecurity, type: 'social_security' },
    { source: 'Investments (your goal)', amount: projection.investmentIncomeRequired, type: 'goal' },
    { source: 'Investments (current savings)', amount: projection.sustainablePortfolioIncome, type: 'supported' },
    { source: 'Other Income', amount: projection.otherIncome, type: 'other' },
  ];

  const totalStatus = overallFundingStatus(projection);

  return (
    <div className="rge-results-table">
      <div className="rge-results-row rge-results-row--header">
        <div>Source</div>
        <div>Estimated Annual Income</div>
        <div>Percent of Target</div>
        <div>Funding Status</div>
      </div>
      {rows.map((row) => {
        const pct = projection.targetIncome > 0 ? (row.amount / projection.targetIncome) * 100 : 0;
        const status = portfolioFundingStatus(projection, row.type);
        return (
          <div key={row.source} className="rge-results-row">
            <div>{row.source}</div>
            <div>{formatUsd(row.amount)}</div>
            <div>{row.amount > 0 ? `${Math.round(pct)}%` : '—'}</div>
            <div>
              <span className={`rge-status ${status.className}`}>
                {status.label}
              </span>
            </div>
          </div>
        );
      })}
      <div className="rge-results-row rge-results-row--total">
        <div>Total (supported)</div>
        <div>{formatUsd(projection.sustainableTotalIncome)}</div>
        <div>{Math.round(projection.fundingPercent)}%</div>
        <div>
          <span className={`rge-status ${totalStatus.className}`}>
            {totalStatus.label}
          </span>
        </div>
      </div>
      <div className="rge-results-row rge-results-row--goal">
        <div>Total (goal)</div>
        <div>{formatUsd(projection.targetIncome)}</div>
        <div>100%</div>
        <div>
          <span className="rge-status rge-status--funded">Target</span>
        </div>
      </div>
    </div>
  );
}

function ProjectionChart({
  series,
  retirementAge,
  sustainablePortfolioIncome,
  investmentIncomeRequired,
  desiredDepletionAge,
  lastsThrough95,
  targetIncome,
  socialSecurity,
  annualFundingGap,
  fundingPercent,
  incomeNarrative,
  hoveredAge,
  onHover,
}) {
  const width = 820;
  const height = 360;
  const pad = { left: 76, right: 36, top: 44, bottom: 48 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const startAge = series[0].age;
  const endAge = CHART_END_AGE;
  const balanceMax = Math.max(
    1,
    ...series.flatMap((row) => [
      row.sustainableBalance ?? 0,
      row.desiredBalance ?? 0,
    ]),
  );
  const yMax = Math.ceil(balanceMax / 50000) * 50000;
  const x = (age) => pad.left + ((age - startAge) / Math.max(1, endAge - startAge)) * plotWidth;
  const y = (value) => pad.top + plotHeight - (value / yMax) * plotHeight;

  const buildPath = (key) => {
    const points = series.filter((row) => row[key] != null);
    if (!points.length) return '';
    return points.map((row, index) => `${index === 0 ? 'M' : 'L'} ${x(row.age).toFixed(1)} ${y(row[key]).toFixed(1)}`).join(' ');
  };

  const sustainablePath = buildPath('sustainableBalance');
  const desiredPath = buildPath('desiredBalance');
  const labels = [startAge, retirementAge, endAge]
    .filter((age, index, arr) => age >= startAge && age <= endAge && arr.indexOf(age) === index);
  const retirementX = x(retirementAge);
  const depletionX = desiredDepletionAge != null ? x(desiredDepletionAge) : null;
  const endX = x(endAge);

  const hoveredPoint = series.find((row) => row.age === hoveredAge) || null;

  const handlePointer = (event) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const relativeX = ((event.clientX - rect.left) / rect.width) * width;
    const age = startAge + ((relativeX - pad.left) / plotWidth) * (endAge - startAge);
    const nearest = series.reduce((best, row) => (
      Math.abs(row.age - age) < Math.abs(best.age - age) ? row : best
    ), series[0]);
    onHover(nearest.age);
  };

  return (
    <div className="rge-chart-wrap">
      <h3>Retirement Projection</h3>
      <p>
        Two paths from the same retirement balance: what your current savings can sustainably support (blue)
        versus your desired lifestyle spending (orange).
      </p>
      <div className="rge-chart-container">
        {hoveredPoint ? (
          <div
            className="rge-chart-tooltip"
            style={{
              left: `${((x(hoveredPoint.age) / width) * 100).toFixed(2)}%`,
              top: '12%',
            }}
          >
            <strong>Age {hoveredPoint.age}</strong>
            {hoveredPoint.sustainableBalance != null ? (
              <span>Current savings path: {formatUsd(hoveredPoint.sustainableBalance)}</span>
            ) : null}
            {hoveredPoint.desiredBalance != null ? (
              <span>Desired lifestyle: {formatUsd(hoveredPoint.desiredBalance)}</span>
            ) : hoveredPoint.age > (desiredDepletionAge ?? endAge) ? (
              <span>Desired lifestyle: depleted</span>
            ) : null}
          </div>
        ) : null}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Retirement portfolio projection comparing sustainable income path and desired lifestyle path"
          onMouseMove={handlePointer}
          onMouseLeave={() => onHover(null)}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
            <g key={`wealth-${tick}`}>
              <line className="rge-grid" x1={pad.left} x2={width - pad.right} y1={y(yMax * tick)} y2={y(yMax * tick)} />
              <text className="rge-axis" x={pad.left - 8} y={y(yMax * tick) + 4} textAnchor="end">
                {formatUsdCompact(yMax * tick)}
              </text>
            </g>
          ))}
          {labels.map((age) => (
            <text key={age} className="rge-axis" x={x(age)} y={height - 14} textAnchor="middle">{age}</text>
          ))}
          <text className="rge-axis" x={width / 2} y={height - 2} textAnchor="middle">Age</text>
          <text
            className="rge-axis rge-axis--ylabel"
            x={16}
            y={pad.top + plotHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90 16 ${pad.top + plotHeight / 2})`}
          >
            Portfolio balance (today&apos;s $)
          </text>
          {!lastsThrough95 && depletionX != null ? (
            <rect
              className="rge-depletion-shade"
              x={depletionX}
              y={pad.top}
              width={Math.max(0, endX - depletionX)}
              height={plotHeight}
            />
          ) : null}
          <line className="rge-retirement-line" x1={retirementX} x2={retirementX} y1={pad.top} y2={pad.top + plotHeight} />
          <text className="rge-retirement-label" x={retirementX + 4} y={pad.top + 12}>
            Retire {retirementAge}
          </text>
          <path d={sustainablePath} className="rge-line rge-line--sustainable" />
          <path d={desiredPath} className="rge-line rge-line--desired" />
          {!lastsThrough95 && desiredDepletionAge != null ? (
            <>
              <circle
                className="rge-chart-dot rge-chart-dot--depletion"
                cx={x(desiredDepletionAge)}
                cy={y(0)}
                r={5}
              />
              <text
                className="rge-depletion-label"
                x={x(desiredDepletionAge)}
                y={pad.top + plotHeight + 28}
                textAnchor="middle"
              >
                Runs out at age {desiredDepletionAge}
              </text>
            </>
          ) : null}
          {hoveredPoint && hoveredPoint.sustainableBalance != null ? (
            <circle
              className="rge-chart-dot rge-chart-dot--sustainable"
              cx={x(hoveredPoint.age)}
              cy={y(hoveredPoint.sustainableBalance)}
              r={6}
            />
          ) : null}
          {hoveredPoint && hoveredPoint.desiredBalance != null ? (
            <circle
              className="rge-chart-dot rge-chart-dot--desired"
              cx={x(hoveredPoint.age)}
              cy={y(hoveredPoint.desiredBalance)}
              r={6}
            />
          ) : null}
        </svg>
      </div>
      <div className="rge-legend">
        <span><i className="rge-swatch rge-swatch--sustainable" />Current Savings Support — {formatUsd(sustainablePortfolioIncome)}/yr through age {endAge}</span>
        <span><i className="rge-swatch rge-swatch--desired" />Desired Retirement Lifestyle — {formatUsd(investmentIncomeRequired)}/yr from investments</span>
      </div>
      <p className="rge-chart-disclaimer">
        Illustrative only — {formatPct(NOMINAL_RETURN)} return while saving, {formatPct(PRE_RETIREMENT_NOMINAL_RETURN)} in the final {GLIDE_PATH_YEARS} years before retirement, {formatPct(INFLATION, 1)} inflation, all figures in today&apos;s dollars, modeled through age {endAge}.
      </p>
      <div className="rge-income-summary">
        <p className="rge-income-summary-title">Retirement Income Summary</p>
        <div className="rge-income-summary-grid">
          <div><span>Desired retirement income</span><strong>{formatUsd(targetIncome)}</strong></div>
          <div><span>Social Security</span><strong>{formatUsd(socialSecurity)}</strong></div>
          <div><span>Investment income required</span><strong>{formatUsd(investmentIncomeRequired)}</strong></div>
          <div><span>Current portfolio supports</span><strong>{formatUsd(sustainablePortfolioIncome)}</strong></div>
          <div><span>Income replacement achieved</span><strong>{Math.round(fundingPercent)}%</strong></div>
          <div><span>Funding gap</span><strong>{formatUsd(annualFundingGap)}/yr</strong></div>
          <div className="rge-income-summary-highlight">
            <span>Projected depletion</span>
            <strong>
              {lastsThrough95
                ? `Portfolio lasts through age ${endAge}`
                : `Age ${desiredDepletionAge}`}
            </strong>
          </div>
        </div>
        {incomeNarrative ? (
          <p className="rge-income-narrative">{incomeNarrative}</p>
        ) : null}
      </div>
    </div>
  );
}
